import { useTranslation } from 'react-i18next';
import { ModalScreenPension } from '../../general/ModalScreen';
import { OverlayHeader } from '../OverlayHeader';
import { useContext, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { reverseFormatNumber, toPrice } from '../../../utils/parsers';
import { Button } from '../../general/Button';
import { SelectInput } from '../../general/form-inputs/SelectInput';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import { IconElements } from '../../../config/icons';
import { Account, PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { AccountReturnVoid } from '../cocktail-filler-screen/SupplementSection';
import { DateTime } from 'luxon';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { MIN_AGE_CLIENT, riskProfiles } from '../../../config/constants';
import { formCompleted, useAccountBalancePrediction } from '../hooks/AccountBalancePredictions';
import { getPerMonthFactor, toFullEurosAsCents } from '../../../utils/functions';
import { useGetPerMonthFactorValues } from '../../../hooks/useGetPerMonthFactorValues';
import { useYearsUntilPensionAge } from '../hooks/useYearsUntilPensionAge';

export type SupplementQuestionType = 'currentValue' | 'periodicDeposit' | 'investmentStrategy' | 'dateAvailable';

export const SupplementGeneralModal: React.FC<{
    isShown: boolean;
    isPartnerSection: boolean;
    account: Account;
    updateAccount: (account: Account) => void;
    onClose: () => void;
    onlyAvailableAtPensionDate?: boolean;
    isResultSection?: boolean;
}> = ({
    isShown,
    onClose,
    updateAccount,
    account,
    isPartnerSection,
    isResultSection = false,
    onlyAvailableAtPensionDate = false,
}) => {
    const { t } = useTranslation('pension');
    const {
        actions,
        parameters: { personalInfo },
    } = useContext(PensionContext);

    const [currentAccount, setCurrentAccount] = useState<Account>({
        ...account,
        riskProfile: account.riskProfile ?? riskProfiles.default,
    });

    const nextButtonEnabled = (currentAccount: Account) =>
        formCompleted(currentAccount, onlyAvailableAtPensionDate) &&
        (currentAccount.type === 'investment' ? !!computationResulAtPensionDate : true) &&
        !!computationResultAtPensionMonthly &&
        !!computationResulAtPensionDate;

    const yearsUntilPensionAge = useYearsUntilPensionAge(isPartnerSection);
    const retirementYear = DateTime.now().year + yearsUntilPensionAge;

    const prediction = useAccountBalancePrediction(currentAccount, onlyAvailableAtPensionDate, isPartnerSection, true);
    const computationResulAtPensionDate = prediction.computationResultAtPension;
    const computationResultEndOfAccount = prediction.computationResultAtAccountEnd;
    const forecastRetirementQuery = useGetPerMonthFactorValues(
        isPartnerSection ? personalInfo.partner?.age ?? MIN_AGE_CLIENT : personalInfo.self?.age ?? MIN_AGE_CLIENT,
    );

    const perMonthFactor = getPerMonthFactor(forecastRetirementQuery.data);
    const computationResultAtPensionMonthly = computationResulAtPensionDate / perMonthFactor;

    const updateModalAccountData = (changeAccount: Account) => {
        setCurrentAccount(changeAccount);
    };

    const applyAccount = () => {
        const updatedAccount = {
            ...currentAccount,
            predictedAmount: computationResultEndOfAccount,
            predictedAmountMonthly: computationResultAtPensionMonthly,
            predictedAmountAtEndAccount: toFullEurosAsCents(computationResultEndOfAccount),
            changedManually: false,
        } as Account;
        updateAccount(updatedAccount);
        actions.setParameters({});
        onClose();
    };

    const getTitle = () => {
        if (isResultSection) {
            return t('pension-cocktail-fill-screen-results-save-or-invest-modal-title', {
                saveOrInvest:
                    currentAccount.type === 'saving'
                        ? t('pension-cocktail-fill-screen-results-save-or-invest-modal-title-save')
                        : t('pension-cocktail-fill-screen-results-save-or-invest-modal-title-invest'),
            });
        }

        return isPartnerSection
            ? t('pension-cocktail-fill-screen-supplement-partner-modal-title')
            : t('pension-cocktail-fill-screen-supplement-modal-title');
    };

    const getCurrentValueLabel = () => {
        if (isResultSection) {
            return t('pension-cocktail-fill-screen-results-save-or-invest-modal-first-deposit');
        }
        return isPartnerSection
            ? t('pension-cocktail-fill-screen-supplement-partner-modal-amount-label')
            : t('pension-cocktail-fill-screen-supplement-modal-amount-label');
    };

    const getInvestmentStrategyLabel = () => {
        if (isResultSection) {
            return t('pension-cocktail-fill-screen-results-save-or-invest-modal-risk-strategy');
        }
        return isPartnerSection
            ? t('pension-cocktail-fill-screen-supplement-partner-modal-risk-profile-label')
            : t('pension-cocktail-fill-screen-supplement-modal-risk-profile-label');
    };

    const GetViewDataAtAccountEnd = () => {
        const dateDdMmYyy = currentAccount.availableAt
            ? DateTime.fromISO(currentAccount.availableAt).toFormat('dd-LL-yyyy')
            : '';
        return (
            <>
                <span className="font-medium">
                    {t('pension-cocktail-fill-screen-results-save-or-invest-modal-exp-returns', { year: dateDdMmYyy })}
                </span>
                <span>
                    {toPrice(computationResultEndOfAccount, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })}
                </span>
                <span></span>
            </>
        );
    };

    const GetViewDataAtPensionEnd = () => {
        return (
            <>
                <span className="font-medium">
                    {t('pension-cocktail-fill-screen-results-save-or-invest-modal-exp-returns', {
                        year: retirementYear,
                    })}
                </span>
                <span>
                    {toPrice(computationResulAtPensionDate, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })}
                </span>
                <span>
                    {toPrice(computationResultAtPensionMonthly, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })}
                </span>
            </>
        );
    };

    const TotalAmountsSection = () => (
        <div className="grid grid-cols-3 gap-4 border-t pt-5 flex-wrap sm:items-end">
            <span></span>
            <span className="font-medium">
                {t('pension-cocktail-fill-screen-results-save-or-invest-modal-exp-total')}
            </span>
            <span className="font-medium">
                {t('pension-cocktail-fill-screen-results-save-or-invest-modal-exp-monthly')}
            </span>
            {!isResultSection && <GetViewDataAtAccountEnd />}
            <GetViewDataAtPensionEnd />
        </div>
    );
    return (
        <ModalScreenPension isShown={isShown}>
            <div className="bg-white md:mx-auto md:rounded-xl p-4 sm:p-8">
                <OverlayHeader title={getTitle()} onClose={onClose} />
                <div className="flex flex-col gap-4 mt-8">
                    <SupplementModalInputContainer
                        title={getCurrentValueLabel()}
                        inputType="currentValue"
                        currentAccount={currentAccount}
                        onChange={updateModalAccountData}
                    />
                    <SupplementModalInputContainer
                        title={
                            isPartnerSection
                                ? t('pension-cocktail-fill-screen-supplement-partner-modal-periodic-deposit-label')
                                : t('pension-cocktail-fill-screen-supplement-modal-periodic-deposit-label')
                        }
                        inputType="periodicDeposit"
                        currentAccount={currentAccount}
                        onChange={updateModalAccountData}
                    />
                    {currentAccount.type === 'investment' && (
                        <SupplementModalInputContainer
                            title={getInvestmentStrategyLabel()}
                            inputType="investmentStrategy"
                            currentAccount={currentAccount}
                            onChange={updateModalAccountData}
                            tooltip={{
                                text: t('pension-cocktail-fill-screen-supplement-modal-periodic-deposit-tooltip-text'),
                                id: 'investment-strategy-tooltip-id',
                            }}
                        />
                    )}
                    {!onlyAvailableAtPensionDate && (
                        <SupplementModalInputContainer
                            title={t('pension-cocktail-fill-screen-supplement-modal-date-label')}
                            inputType="dateAvailable"
                            currentAccount={currentAccount}
                            onChange={updateModalAccountData}
                            isPartnerSection={isPartnerSection}
                        />
                    )}
                    {currentAccount.type === 'investment' &&
                        (prediction.computationResultAtPension !== 0 || prediction.isInvestmentLoading) && (
                            <LoadingStateWrapper
                                isLoading={prediction.isInvestmentLoading}
                                isError={prediction.isInvestmentError}
                                isIdle={prediction.isInvestmentIdle}
                                isFetching={prediction.isInvestmentFetching}
                            >
                                <TotalAmountsSection />
                            </LoadingStateWrapper>
                        )}
                    {currentAccount.type === 'saving' && prediction.computationResultAtPension !== 0 && (
                        <>
                            <TotalAmountsSection />
                        </>
                    )}
                    <div className="flex gap-2 sm:gap-4 flex-row flex-wrap justify-start mt-8">
                        <Button className="w-30 sm:w-44" buttonType="secondary" onClick={onClose}>
                            {t('general-cancel')}
                        </Button>
                        <Button
                            className={`w-30 sm:w-44`}
                            buttonType="primary"
                            disabled={!nextButtonEnabled(currentAccount)}
                            onClick={applyAccount}
                        >
                            {t('general-apply')}
                        </Button>
                    </div>
                </div>
            </div>
        </ModalScreenPension>
    );
};

export const SupplementModalInputContainer: React.FC<{
    title: string;
    inputType: SupplementQuestionType;
    currentAccount: Account;
    tooltip?: { title?: string; text: string; id: string };
    onChange: (account: Account) => void;
    isPartnerSection?: boolean;
}> = ({ title, inputType, currentAccount, tooltip, onChange, isPartnerSection }) => {
    return (
        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
            <span className="w-40 md:w-56">{title}</span>
            {inputType === 'currentValue' && (
                <SupplementModalCurrentValue currentAccount={currentAccount} onChange={onChange} />
            )}
            {inputType === 'periodicDeposit' && (
                <SupplementModalPeriodicDepositValue currentAccount={currentAccount} onChange={onChange} />
            )}
            {inputType === 'periodicDeposit' && (
                <SupplementModalPeriodicDepositPeriod currentAccount={currentAccount} onChange={onChange} />
            )}
            {inputType === 'investmentStrategy' && (
                <SupplementModalInvestmentStrategy currentAccount={currentAccount} onChange={onChange} />
            )}
            {inputType === 'dateAvailable' && isPartnerSection !== undefined && (
                <SupplementModalDateAvailable
                    currentAccount={currentAccount}
                    onChange={onChange}
                    isPartnerSection={isPartnerSection}
                />
            )}
            {!!tooltip && (
                <Tooltip tooltipTitle={tooltip.title} tooltipText={tooltip.text}>
                    <TooltipIndicator>?</TooltipIndicator>
                </Tooltip>
            )}
        </div>
    );
};

const SupplementModalCurrentValue: React.FC<{
    currentAccount: Account;
    onChange?: AccountReturnVoid;
}> = ({ currentAccount, onChange }) => {
    const { t: tg } = useTranslation('general');

    return (
        <CurrencyInput
            type="text"
            className="w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium"
            fixedDecimalLength={0}
            decimalsLimit={2}
            placeholder={tg('euro-placeholder')}
            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
            allowDecimals={false}
            value={!!currentAccount.currentAmount ? (currentAccount.currentAmount ?? 0) / 100 : undefined}
            onValueChange={(value = '') => {
                if (onChange) {
                    onChange({ ...currentAccount, currentAmount: reverseFormatNumber(value, 'nl-NL') * 100 });
                }
            }}
        />
    );
};

const SupplementModalPeriodicDepositValue: React.FC<{
    currentAccount: Account;
    onChange?: AccountReturnVoid;
}> = ({ currentAccount, onChange }) => {
    const { t: tg } = useTranslation('general');

    return (
        <CurrencyInput
            type="text"
            className="w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium"
            fixedDecimalLength={0}
            decimalsLimit={2}
            placeholder={tg('euro-placeholder')}
            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
            allowDecimals={false}
            value={
                !!currentAccount.recurringPaymentAmount ? (currentAccount.recurringPaymentAmount ?? 0) / 100 : undefined
            }
            onValueChange={(value = '') => {
                if (onChange) {
                    onChange({ ...currentAccount, recurringPaymentAmount: reverseFormatNumber(value, 'nl-NL') * 100 });
                }
            }}
        />
    );
};

const SupplementModalPeriodicDepositPeriod: React.FC<{
    currentAccount: Account;
    onChange?: AccountReturnVoid;
}> = ({ currentAccount, onChange }) => {
    const { t } = useTranslation('pension');
    return (
        <SelectInput
            className="w-36"
            selected={currentAccount.recurringPaymentInterval}
            onChange={(value) => {
                if (onChange) {
                    onChange({ ...currentAccount, recurringPaymentInterval: value as 'year' | 'month' });
                }
            }}
            values={[
                {
                    label: t('general-per-month'),
                    value: 'month',
                },
                {
                    label: t('general-per-year'),
                    value: 'year',
                },
            ]}
        />
    );
};

const SupplementModalInvestmentStrategy: React.FC<{
    currentAccount: Account;
    onChange?: AccountReturnVoid;
}> = ({ currentAccount, onChange }) => {
    const { t } = useTranslation('pension');

    return (
        <SelectInput
            className="w-40 md:w-56"
            onChange={(value) => {
                if (onChange) {
                    onChange({ ...currentAccount, riskProfile: value });
                }
            }}
            selected={currentAccount.riskProfile || riskProfiles.default}
            values={[
                {
                    label: t('general-very-defensive'),
                    value: riskProfiles.veryDefensive,
                },
                {
                    label: t('general-defensive'),
                    value: riskProfiles.defensive,
                },
                {
                    label: t('general-neutral'),
                    value: riskProfiles.neutral,
                },
                {
                    label: t('general-offensive'),
                    value: riskProfiles.offensive,
                },
                {
                    label: t('general-very-offensive'),
                    value: riskProfiles.veryOffensive,
                },
            ]}
        />
    );
};

const SupplementModalDateAvailable: React.FC<{
    currentAccount: Account;
    onChange: AccountReturnVoid;
    isPartnerSection: boolean;
}> = ({ currentAccount, onChange, isPartnerSection }) => {
    const { t } = useTranslation('general');
    const [newDate, setNewDate] = useState<string | undefined>(currentAccount.availableAt);

    const newDateIsInTheFuture = (newDateString: string) => {
        const newDate = DateTime.fromISO(newDateString);
        const now = DateTime.now();
        return newDate >= now.endOf('day');
    };

    const Autofill = (newDate: string) => {
        setNewDate(newDate);
        onChange({ ...currentAccount, availableAt: newDate });
    };

    return (
        <>
            <div className="relative w-40 md:w-56">
                <input
                    className={`${
                        !!currentAccount.availableAt ? 'text-black' : 'text-gray-400'
                    } w-full py-2 px-4 border rounded-lg focus:outline-hidden bg-white border-gray-200 font-medium`}
                    placeholder={t('date-placeholder')}
                    type="Date"
                    value={newDate ?? ''}
                    required
                    onChange={(e) => {
                        const validDate = newDateIsInTheFuture(e.target.value);
                        setNewDate(e.target.value);
                        if (validDate) {
                            onChange({ ...currentAccount, availableAt: e.target.value });
                        }
                    }}
                    onBlur={(e) => {
                        const validDate = newDateIsInTheFuture(e.target.value);
                        if (!validDate) {
                            // BND forecast endpoint only accepts dates in the future
                            const tomorrow = DateTime.now().plus({ day: 1 }).toFormat('yyyy-LL-dd');
                            setNewDate(tomorrow);
                            onChange({ ...currentAccount, availableAt: tomorrow });
                        }
                    }}
                ></input>
                <IconElements.Calendar className="absolute top-2 right-4" />
            </div>
            <AutoFillMoneyAvailableAtDateWithPensionDate onClick={Autofill} isPartnerSection={isPartnerSection} />
        </>
    );
};

export const AutoFillMoneyAvailableAtDateWithPensionDate: React.FC<{
    onClick: (pensionDate: string) => void;
    yearsUntilPensionAge?: number;
    isPartnerSection: boolean;
}> = ({ onClick, isPartnerSection }) => {
    const { t } = useTranslation('pension');
    const yearsUntilPensionAge = useYearsUntilPensionAge(isPartnerSection);
    const onClickAutoFill = () => {
        if (yearsUntilPensionAge !== undefined) {
            const pensionDate = DateTime.now()
                .plus({
                    years: yearsUntilPensionAge,
                })
                .toFormat('yyyy-LL-dd');

            onClick(pensionDate);
        }
    };
    const autoFillPensionAgeText = isPartnerSection
        ? t('pension-cocktail-fill-screen-supplement-date-autofill-label-partner')
        : t('pension-cocktail-fill-screen-supplement-date-autofill-label-self');

    return (
        <Button buttonType="internalLinkGreen" onClick={onClickAutoFill}>
            {autoFillPensionAgeText}
        </Button>
    );
};
